'use client'
import { msg } from '@lingui/core/macro'
import { BookIcon, CookieIcon, ScaleIcon } from 'lucide-react'
import * as React from 'react'
import { useLingui } from '@lingui/react'
import { motion } from 'framer-motion'

import { GridSection } from '@/components/marketing/fragments/grid-section'
import { SiteHeading } from '@/components/marketing/fragments/site-heading'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion'
import { Grid } from '@/components/blocks/feature-section-with-card-gradient'
import { AnimatedGridPattern } from '@/components/ui/animated-grid-pattern'

interface PolicyCardProps {
  title: string
  content: string
  icon: React.ReactNode
}

const PolicyCard: React.FC<PolicyCardProps> = ({ title, content, icon }) => (
  <div className='group relative transition-all duration-500'>
    {/* Content */}
    <div className='relative z-20 p-6 sm:p-8 overflow-hidden bg-[rgba(255,255,255,0.05)] backdrop-blur-[10px] shadow-lg rounded-3xl transition-all duration-500 group-hover:scale-105'>
      <Grid size={20} />
      <div className='flex flex-col items-center text-center space-y-4'>
        <div className='text-4xl text-blue-500 group-hover:text-orange-500 transition-colors duration-300'>
          {icon}
        </div>
        <h3 className='text-lg sm:text-xl font-bold text-foreground'>{title}</h3>
        <p className='text-sm sm:text-base text-muted-foreground'>{content}</p>
      </div>
    </div>
  </div>
)

export function CookiePolicy(): React.JSX.Element {
  const { i18n } = useLingui()

  const DATA_CARDS = [
    {
      title: msg`What are Cookies?`,
      icon: <CookieIcon className='size-13 shrink-0' />,
      content: msg`Cookies are small text files stored on your device that help us improve your experience by remembering preferences.`
    },
    {
      title: msg`Types of Cookies`,
      icon: <BookIcon className='size-13 shrink-0' />,
      content: msg`We use both session and persistent cookies to track user activity and enhance site functionality.`
    },
    {
      title: msg`Managing Cookies`,
      icon: <ScaleIcon className='size-13 shrink-0' />,
      content: msg`You can control cookie settings in your browser. However, disabling cookies may impact your experience on our site.`
    }
  ]

  const DATA_ACCORDION = [
    {
      title: msg`Cookies We Use`,
      content: msg`We use cookies for functionality (e.g., login sessions), performance (e.g., analytics), and advertising (e.g., targeted ads).`
    },
    {
      title: msg`Third-Party Cookies`,
      content: msg`We may allow third-party services (such as Google Analytics) to place cookies on your device for specific purposes.`
    },
    {
      title: msg`How to Manage Cookies`,
      content: msg`You can adjust cookie settings in your browser. For more detailed instructions, refer to your browser’s help guide.`
    },
    {
      title: msg`Changes to Our Cookie Policy`,
      content: msg`We may update this Cookie Policy from time to time. Any changes will be posted on this page with the latest update as of 04:11 PM +07, Friday, June 06, 2025.`
    }
  ]

  return (
    <div className='relative'>
      <AnimatedGridPattern />
      <GridSection>
        <div className='bg-diagonal-lines'>
          <div className='flex flex-col bg-background md:px-12 px-4 py-20 md:py-24 lg:mx-12 lg:border-x overflow-hidden'>
            <SiteHeading
              badge={msg`Legal`}
              title={msg`Cookie Policy`}
              description={msg`Learn how we use cookies and similar technologies to enhance your experience on our platform.`}
            />
            <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-10 md:mb-16'>
              {DATA_CARDS.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut', delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <PolicyCard
                    title={i18n._(item.title)}
                    content={i18n._(item.content)}
                    icon={item.icon}
                  />
                </motion.div>
              ))}
            </div>

            <Accordion type='single' collapsible>
              {DATA_ACCORDION.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className='flex items-center justify-between text-lg font-medium'>
                    {i18n._(item.title)}
                  </AccordionTrigger>
                  <AccordionContent className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                    {i18n._(item.content)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </GridSection>
    </div>
  )
}
